import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
    type MutableRefObject,
    type ReactNode
} from "react";

import { css } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";
import { FilterableGalleryProvider } from "src/uniform/contexts/filterable-gallery-context";
import {
    FilterableItemIndexContext,
    FilterableItemRegistryProvider
} from "src/uniform/contexts/filterable-item-registry-context";

import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import {
    ColumnLayout,
    ColumnLayoutProps
} from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { subscribeToShowAllFilterableGalleryItems } from "../../filterable-gallery-display-limit-override";

import { FILTER_DROPDOWN_DATA_ATTR } from "./components/filter-control";
import { Toolbar } from "./components/toolbar";
import { useFilterState } from "./hooks/use-filter-state";
import { useOutsideClick } from "./hooks/use-outside-click";
import {
    applyFilters,
    applySearch,
    applySort,
    buildLabelMaps
} from "./utils/filterEngine";

export interface UniformFilterableGalleryProps {
    /** Dot-paths for free-text search. Omit to hide the search bar. */
    readonly searchFieldPaths?: string[];
    /** Controls whether the built-in toolbar search input renders. Search state still uses searchFieldPaths. */
    readonly showSearchInput?: boolean | { value?: boolean };
    /** Maximum number of filtered items to render. Omit to render all. */
    readonly displayLimit?: number | string | { value?: number | string };
    /** Uniform Block param holding FilterDefinition children. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly filters?: any;
    /** Uniform Block param holding SortDefinition children. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly sorts?: any;
    /** When true (default), filter/search/sort state is mirrored to the URL. */
    readonly urlSync?: boolean;
    /** Column layout pattern (e.g. "4-4-4" for 3 columns, "3-3-3-3" for 4). */
    readonly layout?: ColumnLayoutProps["layout"];
    /**
     * Uniform Block param holding FacetDataSource children. Each entry has a
     * `sourceId` (matching a FilterDefinition ID) and a `data` field bound to a
     * separate data resource (e.g. Query - Template Categories at $.items).
     * Unwrapped via flattenValues into Record<string, unknown[]> at runtime.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly facetDataSources?: any;
}

interface FilterableGallerySlotProps extends UniformSlotWrapperComponentProps {
    itemDataArrayRef: MutableRefObject<unknown[]>;
    rawSearchFieldPaths?: string[];
    showSearchInput?: boolean | { value?: boolean };
    displayLimit?: number | string | { value?: number | string };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawFilters?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawSorts?: any;
    urlSync: boolean;
    layout: ColumnLayoutProps["layout"];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawFacetDataSources?: any;
}

const FilterableGallerySlot = ({
    items,
    itemDataArrayRef,
    rawSearchFieldPaths,
    showSearchInput = true,
    displayLimit: rawDisplayLimit,
    rawFilters,
    rawSorts,
    urlSync,
    layout,
    rawFacetDataSources
}: FilterableGallerySlotProps) => {
    const [registeredCount, setRegisteredCount] = useState(0);
    const [displayLimitOverrideQuery, setDisplayLimitOverrideQuery] = useState<
        string | undefined
    >();

    useEffect(() => {
        const currentCount = itemDataArrayRef.current.length;
        if (currentCount !== registeredCount) {
            setRegisteredCount(currentCount);
        }
    }, [itemDataArrayRef, registeredCount]);

    const itemDataArray = itemDataArrayRef.current;

    const searchFieldPaths = useMemo(
        () =>
            Array.isArray(rawSearchFieldPaths)
                ? rawSearchFieldPaths.filter(
                      (path) => typeof path === "string" && path
                  )
                : [],
        [rawSearchFieldPaths]
    );

    const shouldShowSearchInput = resolveBooleanParameter(
        showSearchInput,
        true
    );
    const displayLimit = resolvePositiveIntegerParameter(rawDisplayLimit);

    const filterDefs = useMemo<FilterDefinition[]>(() => {
        if (!rawFilters) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const flat = flattenValues(rawFilters as any) as FilterDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawFilters]);

    const sortDefs = useMemo<SortDefinition[]>(() => {
        if (!rawSorts) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const flat = flattenValues(rawSorts as any) as SortDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawSorts]);

    const optionsDataSources = useMemo<Record<string, unknown[]>>(() => {
        if (!rawFacetDataSources) return {};
        const rawEntries: unknown[] = Array.isArray(rawFacetDataSources)
            ? rawFacetDataSources
            : // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (rawFacetDataSources as any)?.value ?? [];
        if (!Array.isArray(rawEntries) || rawEntries.length === 0) return {};

        return Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rawEntries.flatMap((entry: any) => {
                const sourceId = entry?.fields?.sourceId?.value;
                const data = entry?.fields?.data?.value;
                if (typeof sourceId !== "string" || !Array.isArray(data)) {
                    return [];
                }
                return [[sourceId, data]];
            })
        );
    }, [rawFacetDataSources]);

    const {
        filterObject,
        searchQuery,
        selectedSortId,
        dropdownVisibility,
        isAnyDropdownOpen,
        setSearchQuery,
        handleFilterButtonClick,
        toggleFilterOption,
        clearAllFilters,
        handleSearchSubmit,
        handleSearchClear,
        handleSortChange,
        closeAllDropdownsAndCommit
    } = useFilterState({
        items: itemDataArray,
        filterDefs,
        urlSync,
        optionsDataSources
    });

    useEffect(
        () =>
            subscribeToShowAllFilterableGalleryItems(({ query }) => {
                setSearchQuery(query);
                setDisplayLimitOverrideQuery(query);
            }),
        [setSearchQuery]
    );

    const sortedMatchingDataItems = useMemo(
        () =>
            applySort(
                applySearch(
                    applyFilters(itemDataArray, filterDefs, filterObject),
                    searchFieldPaths,
                    searchQuery
                ),
                sortDefs.find((sort) => sort.id === selectedSortId)
            ),
        [
            itemDataArray,
            filterDefs,
            filterObject,
            searchFieldPaths,
            searchQuery,
            sortDefs,
            selectedSortId
        ]
    );

    const shouldApplyDisplayLimit =
        typeof displayLimit === "number" &&
        displayLimitOverrideQuery !== searchQuery.trim();

    const visibleDataItems = useMemo(
        () =>
            shouldApplyDisplayLimit
                ? sortedMatchingDataItems.slice(0, displayLimit)
                : sortedMatchingDataItems,
        [displayLimit, shouldApplyDisplayLimit, sortedMatchingDataItems]
    );

    const visibleIndices = useMemo(
        () => getOrderedIndicesForDataItems(itemDataArray, visibleDataItems),
        [itemDataArray, visibleDataItems]
    );

    const visibleIndexSet = useMemo(
        () => new Set(visibleIndices),
        [visibleIndices]
    );

    const labelMaps = useMemo(
        () => buildLabelMaps(itemDataArray, filterDefs, optionsDataSources),
        [itemDataArray, filterDefs, optionsDataSources]
    );

    useOutsideClick({
        isActive: isAnyDropdownOpen,
        insideMarkerAttribute: FILTER_DROPDOWN_DATA_ATTR,
        onOutsideClick: closeAllDropdownsAndCommit
    });

    const isRegistered = registeredCount > 0;

    const orderedItems = useMemo(() => {
        const slotItems = items as ReactNode[];
        if (!isRegistered) {
            return slotItems.map((item, index) => ({
                index,
                item,
                visible: true
            }));
        }

        const visible = visibleIndices.map((index) => ({
            index,
            item: slotItems[index],
            visible: true
        }));
        const hidden = slotItems
            .map((item, index) => ({ index, item, visible: false }))
            .filter(({ index }) => !visibleIndexSet.has(index));

        return [...visible, ...hidden];
    }, [isRegistered, items, visibleIndices, visibleIndexSet]);

    const filteredItems = useMemo(
        () =>
            orderedItems
                .filter(({ visible }) => visible)
                .map(({ item }) => item),
        [orderedItems]
    );

    return (
        <>
            <ColumnLayout layout="12">
                <Toolbar
                    filterDefs={filterDefs}
                    sortDefs={sortDefs}
                    searchFieldPaths={searchFieldPaths}
                    showSearchInput={shouldShowSearchInput}
                    filterObject={filterObject}
                    searchQuery={searchQuery}
                    selectedSortId={selectedSortId}
                    dropdownVisibility={dropdownVisibility}
                    labelMaps={labelMaps}
                    onSearchChange={setSearchQuery}
                    onSearchSubmit={handleSearchSubmit}
                    onSearchClear={handleSearchClear}
                    onFilterButtonClick={handleFilterButtonClick}
                    onOptionToggle={toggleFilterOption}
                    onClearAllFilters={clearAllFilters}
                    onSortChange={handleSortChange}
                />
            </ColumnLayout>
            <FilterableGalleryProvider
                items={items}
                filteredItems={filteredItems}
            >
                <div css={gallerySlotStyle}>
                    {isRegistered &&
                    sortedMatchingDataItems.length === 0 &&
                    itemDataArray.length > 0 ? (
                        <p
                            css={emptyStateStyle}
                            data-testid="filterable-gallery-empty"
                        >
                            No items match your filters.
                        </p>
                    ) : null}
                    <ColumnLayout layout={layout} renderAsList="ul">
                        {orderedItems.map(({ index, item, visible }) => (
                            <FilterableItemIndexContext.Provider
                                key={extractKey(item, index)}
                                value={index}
                            >
                                <li
                                    hidden={visible ? undefined : true}
                                    css={galleryItemStyle}
                                    data-testid={
                                        visible
                                            ? "filterable-gallery-item"
                                            : undefined
                                    }
                                >
                                    {item}
                                </li>
                            </FilterableItemIndexContext.Provider>
                        ))}
                    </ColumnLayout>
                </div>
            </FilterableGalleryProvider>
        </>
    );
};

const UniformFilterableGallery = ({
    searchFieldPaths: rawSearchFieldPaths,
    showSearchInput = true,
    displayLimit,
    filters: rawFilters,
    sorts: rawSorts,
    urlSync = true,
    layout = "4-4-4",
    facetDataSources: rawFacetDataSources
}: UniformFilterableGalleryProps): JSX.Element => {
    const itemDataArrayRef = useRef<unknown[]>([]);
    const [, notifyItemDataChanged] = useReducer((version) => version + 1, 0);
    const handleRegistryChange = useCallback((data: unknown[]) => {
        const previousData = itemDataArrayRef.current;
        if (
            data.length !== previousData.length ||
            data.some((item, index) => item !== previousData[index])
        ) {
            itemDataArrayRef.current = data;
            notifyItemDataChanged();
        }
    }, []);

    const wrapperComponent = useCallback(
        (slotProps: UniformSlotWrapperComponentProps) => (
            <FilterableGallerySlot
                {...slotProps}
                itemDataArrayRef={itemDataArrayRef}
                rawSearchFieldPaths={rawSearchFieldPaths}
                showSearchInput={showSearchInput}
                displayLimit={displayLimit}
                rawFilters={rawFilters}
                rawSorts={rawSorts}
                urlSync={urlSync}
                layout={layout}
                rawFacetDataSources={rawFacetDataSources}
            />
        ),
        [
            rawSearchFieldPaths,
            showSearchInput,
            displayLimit,
            rawFilters,
            rawSorts,
            urlSync,
            layout,
            rawFacetDataSources
        ]
    );

    return (
        <UFOSegment name="uniform-filters">
            <TypographyStyles>
                <FilterableItemRegistryProvider
                    onRegistryChange={handleRegistryChange}
                >
                    <UniformSlot
                        name="filterableItems"
                        wrapperComponent={wrapperComponent}
                    />
                </FilterableItemRegistryProvider>
            </TypographyStyles>
        </UFOSegment>
    );
};

const resolveBooleanParameter = (
    parameter: boolean | { value?: boolean } | undefined,
    defaultValue: boolean
) => {
    if (typeof parameter === "boolean") return parameter;
    if (typeof parameter?.value === "boolean") return parameter.value;
    return defaultValue;
};

const resolvePositiveIntegerParameter = (
    parameter: number | string | { value?: number | string } | undefined
) => {
    const rawValue =
        typeof parameter === "object" && parameter !== null
            ? parameter.value
            : parameter;
    const value = typeof rawValue === "string" ? Number(rawValue) : rawValue;

    if (typeof value !== "number" || !Number.isFinite(value)) return undefined;

    const integerValue = Math.floor(value);
    return integerValue > 0 ? integerValue : undefined;
};

const getOrderedIndicesForDataItems = (
    sourceDataItems: unknown[],
    orderedDataItems: unknown[]
) => {
    const indexMap = new Map<unknown, number[]>();
    sourceDataItems.forEach((dataItem, index) => {
        const indices = indexMap.get(dataItem) ?? [];
        indices.push(index);
        indexMap.set(dataItem, indices);
    });

    return orderedDataItems.flatMap((dataItem) => {
        const indices = indexMap.get(dataItem);
        const index = indices?.shift();
        return index === undefined ? [] : [index];
    });
};

const gallerySlotStyle = css({
    marginBlockStart: token("space.400")
});

const emptyStateStyle = css({
    padding: token("space.200"),
    color: token("color.text.subtlest"),
    fontStyle: "italic",
    textAlign: "center"
});

const galleryItemStyle = css({
    "&:not([hidden])": {
        display: "grid"
    },
    "&[hidden]": {
        display: "none"
    }
});

const extractKey = (item: unknown, fallback: number): string | number => {
    if (typeof item !== "object" || item === null) return fallback;
    const key = (item as { key?: unknown }).key;
    if (typeof key === "string" || typeof key === "number") return key;

    return fallback;
};

export default UniformFilterableGallery;
